import { useContext, useState } from 'react'
import * as Sentry from '@sentry/browser'
import { AuthContext } from '@/context/auth-context'
import { useAppDispatch } from '@/store/store-hooks'
import { showGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType } from '@/constants/constants-ui'
import { fileToPngBlob } from '@/organizations/services/image-edit-functions'
import { deleteOrgLogo, uploadOrgLogo } from '@/organizations/services/org-logo-services'

/**
 * Orchestrates the streamlined org-logo actions: resize + upload a picked file, or delete the
 * current logo, refreshing the logo in context on success. Consumers open the OS file picker and
 * hand the selected file to `uploadLogoFile`, or call `deleteLogo`; no intermediate dialog is shown.
 */
export function useOrgLogoUpload() {
  const dispatch = useAppDispatch()
  const { userAccountData, refreshOrgLogo } = useContext(AuthContext)
  const [isBusy, setIsBusy] = useState(false)

  const showError = (message: string) => {
    void dispatch(showGlobalToast({ type: GlobalToastType.ERROR, message }))
  }

  // Resolves once the upload has been *dispatched*, not when it finishes. Completion and failure
  // are handled in the uploadOrgLogo callbacks below, which clear `isBusy` and refresh the logo.
  // A future `await uploadLogoFile(...)` caller must not read the resolved promise as "upload done."
  const uploadLogoFile = async (file: File) => {
    const orgId = userAccountData?.legacyDriveCompatibleOrganizationId
    if (!orgId) {
      // No org to attach the logo to. Bail before resizing so we skip the wasted work and show a
      // clear message instead of letting uploadOrgLogo throw a generic error after the resize.
      showError('Unable to update the logo without an organization. Please try again.')
      return
    }

    setIsBusy(true)

    try {
      // Resize to the logo target box (same dimensions the change-logo dialog uses).
      const imageBlob = await fileToPngBlob(file, 400, 250)
      uploadOrgLogo({
        orgId,
        imageBlob,
        onProgress: () => {},
        onError: () => {
          setIsBusy(false)
          showError('Failed to upload logo. Please try again.')
        },
        onComplete: async () => {
          // The upload succeeded; the logo is in storage. If the follow-up refresh hits a transient
          // fetch error it returns false and leaves the current logo in place — surface that rather
          // than letting the tile silently look unchanged/reverted.
          const refreshed = await refreshOrgLogo?.()
          if (refreshed === false) {
            showError('Your logo was uploaded, but the preview could not be refreshed. Reload to see it.')
          }
          setIsBusy(false)
        },
      })
    } catch {
      setIsBusy(false)
      showError('Failed to upload logo. Please try again.')
    }
  }

  const deleteLogo = async () => {
    const orgId = userAccountData?.legacyDriveCompatibleOrganizationId
    setIsBusy(true)

    try {
      await deleteOrgLogo(orgId ?? null)
      await refreshOrgLogo?.()
    } catch (error) {
      // Mirror the Sentry capture uploadOrgLogo does internally so delete failures aren't
      // observable only as a toast.
      Sentry.captureException(error)
      showError('Failed to remove logo. Please try again.')
    } finally {
      setIsBusy(false)
    }
  }

  return { isBusy, uploadLogoFile, deleteLogo }
}
